/**
 * 前台商城首页 - 中式典雅风格
 * 配色：宣纸白 (#F8F6F1)、茶褐 (#7D6B55)、竹青 (#6B8E7C)、朱砂红 (#C44536)
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Row,
  Col,
  Card,
  Button,
  Typography,
  Spin,
  Image,
} from 'antd';
import { ArrowRightOutlined, ShopOutlined } from '@ant-design/icons';
import StorefrontLayout from '../../components/storefront/StorefrontLayout';
import { productApi } from '../../api/index';

const { Title, Text } = Typography;

// 中式配色
const COLORS = {
  paper: '#F8F6F1',      // 宣纸白
  teaBrown: '#7D6B55',   // 茶褐
  bambooGreen: '#6B8E7C', // 竹青
  cinnabar: '#C44536',   // 朱砂红 (点缀)
  ink: '#2C2C2C',        // 墨色
  lightInk: '#5A5A5A',   // 淡墨
};

export default function HomePage() {
  const navigate = useNavigate();

  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [catLoading, setCatLoading] = useState(false);

  useEffect(() => {
    fetchFeaturedProducts();
    fetchCategories();
  }, []);

  const fetchFeaturedProducts = async () => {
    setProductsLoading(true);
    try {
      const data = await productApi.list({ page: 1, pageSize: 8 });
      setProducts(Array.isArray(data) ? data : data?.items || data?.list || []);
    } catch {
      setProducts([]);
    } finally {
      setProductsLoading(false);
    }
  };

  const fetchCategories = async () => {
    setCatLoading(true);
    try {
      const data = await productApi.listCategories();
      setCategories(data || []);
    } catch {
      setCategories([]);
    } finally {
      setCatLoading(false);
    }
  };

  const getMinPrice = (product) => {
    if (!product.variants || product.variants.length === 0) return '0.00';
    const prices = product.variants.map((v) => v.price || 0);
    return (Math.min(...prices)).toFixed(2);
  };

  const getMinComparePrice = (product) => {
    if (!product.variants || product.variants.length === 0) return null;
    const comparePrices = product.variants
      .map((v) => v.compareAtPrice || 0)
      .filter((p) => p > 0);
    if (comparePrices.length === 0) return null;
    return (Math.min(...comparePrices)).toFixed(2);
  };

  const getProductImage = (product) => {
    if (product.images && product.images.length > 0) {
      return product.images[0].url;
    }
    return null;
  };

  return (
    <StorefrontLayout
      headerStyle={{
        background: COLORS.paper,
        borderBottom: `1px solid ${COLORS.teaBrown}20`,
      }}
    >
      {/* ===== 顶部横幅区 ===== */}
      <div
        style={{
          background: `linear-gradient(135deg, ${COLORS.paper} 0%, ${COLORS.teaBrown}15 100%)`,
          padding: '64px 24px',
          textAlign: 'center',
        }}
      >
        <div style={{ maxWidth: 900, margin: '0 auto' }}>
          <Title
            level={1}
            style={{
              margin: 0,
              color: COLORS.ink,
              fontSize: '36px',
              letterSpacing: '0.5em',
              fontWeight: 300,
              fontFamily: "'Kaiti SC', 'LXGW WenKai','KaiTi', cursive",
            }}
          >
            悦来悦好
          </Title>
          <Text
            style={{
              display: 'block',
              marginTop: 16,
              color: COLORS.lightInk,
              fontSize: '14px',
              letterSpacing: '0.25em',
            }}
          >
             焕新升级 · 全新体验 
          </Text>
          <div
            style={{
              width: 60,
              height: 2,
              background: COLORS.cinnabar,
              margin: '24px auto 0',
            }}
          />
        </div>
      </div>


      {/* ===== 精选商品区 ===== */}
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '48px 24px' }}>
        <Row justify="space-between" align="middle" style={{ marginBottom: 32 }}>
          <Col>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <Title level={3} style={{ margin: 0, color: COLORS.ink }}>
                精选商品
              </Title>
              <div
                style={{
                  width: 40,
                  height: 1,
                  background: COLORS.cinnabar,
                }}
              />
            </div>
          </Col>
          <Col>
            <Button
              type="text"
              icon={<ArrowRightOutlined />}
              onClick={() => navigate('/products')}
              style={{
                color: COLORS.teaBrown,
                fontSize: '14px',
              }}
            >
              查看全部
            </Button>
          </Col>
        </Row>

        {productsLoading ? (
          <div style={{ textAlign: 'center', padding: 64 }}>
            <Spin size="large" />
          </div>
        ) : (
          <Row gutter={[32, 32]}>
            {products.map((product) => {
              const imgUrl = getProductImage(product);
              return (
                <Col key={product.id} xs={24} sm={12}>
                  <Card
                    hoverable
                    onClick={() => navigate(`/products/${product.slug}`)}
                    cover={
                      <div style={{ height: 400, overflow: 'hidden', background: '#fafafa' }}>
                        {imgUrl ? (
                          <Image
                            src={imgUrl}
                            alt={product.title}
                            height={400}
                            width="100%"
                            style={{
                              objectFit: 'cover',
                              transition: 'transform 0.5s',
                            }}
                            preview={false}
                          />
                        ) : (
                          <div
                            style={{
                              height: '100%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              background: `${COLORS.teaBrown}08`,
                            }}
                          >
                            <ShopOutlined style={{ fontSize: 56, color: `${COLORS.teaBrown}30` }} />
                          </div>
                        )}
                      </div>
                    }
                    bodyStyle={{ padding: '16px' }}
                    style={{
                      border: 'none',
                      borderRadius: 12,
                      boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                      transition: 'transform 0.3s, box-shadow 0.3s',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-6px)';
                      e.currentTarget.style.boxShadow = '0 12px 32px rgba(0,0,0,0.12)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.06)';
                    }}
                  >
                    <div style={{ minHeight: 80 }}>
                      <Text
                        strong
                        ellipsis={{ rows: 2 }}
                        style={{
                          display: 'block',
                          fontSize: '15px',
                          color: COLORS.ink,
                          marginBottom: 8,
                          lineHeight: 1.5,
                        }}
                      >
                        {product.title}
                      </Text>
                      {product.description && (
                        <Text
                          ellipsis={{ rows: 1 }}
                          style={{
                            display: 'block',
                            fontSize: '13px',
                            color: COLORS.lightInk,
                            marginBottom: 8,
                          }}
                        >
                          {product.description}
                        </Text>
                      )}
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                        <Text
                          style={{
                            color: COLORS.cinnabar,
                            fontSize: '18px',
                            fontWeight: 600,
                          }}
                        >
                          ${getMinPrice(product)}
                        </Text>
                        {getMinComparePrice(product) && (
                          <Text
                            delete
                            style={{
                              color: COLORS.lightInk,
                              fontSize: '14px',
                            }}
                          >
                            ${getMinComparePrice(product)}
                          </Text>
                        )}
                      </div>
                    </div>
                  </Card>
                </Col>
              );
            })}
          </Row>
        )}

        {products.length === 0 && !productsLoading && (
          <div style={{ textAlign: 'center', padding: '64px 0', color: COLORS.lightInk }}>
            暂无商品
          </div>
        )}
      </div>

      {/* ===== 底部装饰条 ===== */}
      <div
        style={{
          background: `${COLORS.teaBrown}08`,
          padding: '32px 0px',
          marginTop: 48,
        }}
      >
        <div style={{ maxWidth: 1200, margin: '0 auto', textAlign: 'center' }}>
          <Text style={{ color: COLORS.lightInk, fontSize: '13px', letterSpacing: '0.2em' }}>
            品质保证 · 安心购物 · 发货及时 · 售后无忧
          </Text>
          
        </div>
      </div>
    </StorefrontLayout>
  );
}
